import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ButtonNew from "../../components/Core/Button/ButtonNew";
import DayPickerNew from "../../components/Core/DayPicker/DayPickerNew";
import { SALARY_BATCH_STATUS } from "../../configs/constants/SalaryBatchStatus";
import { SalaryBatch, SalaryBatchFilter } from "../../models/Salary";
import { toast } from "../../utils/toast";
import SalaryBatchFilters from "./components/SalaryBatchFilters";
import { useSalaryBatchFilterOptions } from "./components/useSalaryBatchFilterOptions";

interface ISalaryBatchFormProps {
	salaryBatch?: SalaryBatch;
	onSubmit: (from: Date, to: Date, filter: SalaryBatchFilter) => any;
}

const SalaryBatchForm: React.FC<ISalaryBatchFormProps> = ({ salaryBatch, onSubmit }) => {
	const { t } = useTranslation();

	const [filter, setFilter] = useState<SalaryBatchFilter>({});
	const [batchStartDate, setBatchStartDate] = useState(salaryBatch?.from);
	const [batchEndDate, setBatchEndDate] = useState(salaryBatch?.to);

	const filterOptions = useSalaryBatchFilterOptions();

	useEffect(() => {
		salaryBatch?.from && setBatchStartDate(salaryBatch.from);
		salaryBatch?.to && setBatchEndDate(salaryBatch.to);
		// Batches created before consultant filtering only carry the deprecated
		// single `pool`, so fall back to it
		salaryBatch &&
			setFilter(salaryBatch.filter ?? (salaryBatch.pool ? { pools: [salaryBatch.pool] } : {}));
	}, [salaryBatch]);

	const handleSubmit = () => {
		if (!batchStartDate || !batchEndDate) {
			toast.error(t("MISSING_SALARY_BATCH_INTERVAL"));
			return;
		}

		onSubmit(batchStartDate, dayjs(batchEndDate).locale("sv").endOf("day").toDate(), filter);
	};

	const editable = !salaryBatch || salaryBatch.status === SALARY_BATCH_STATUS.PREVIEW;

	return (
		<div className="h-screen min-w-[275px] max-w-[275px] border-r-2 border-gray-300 pr-4">
			{/* Dates first: they define the run, the filters only narrow it */}
			<div className="mb-6 flex flex-col gap-4">
				<DayPickerNew
					value={batchStartDate}
					onChange={setBatchStartDate}
					disabled={!editable}
					label="salary.start"
					placeholder="DATE_PLACEHOLDER"
				/>

				<DayPickerNew
					value={batchEndDate}
					onChange={setBatchEndDate}
					disabled={!editable}
					label="salary.end"
					placeholder="DATE_PLACEHOLDER"
				/>
			</div>

			{/* Dividers fence the filter block off from the dates above and the
			    submit action below */}
			<hr className="mb-5 border-gray-200" />

			<SalaryBatchFilters
				filter={filter}
				onChange={setFilter}
				options={filterOptions}
				disabled={!editable}
			/>

			<hr className="mb-5 border-gray-200" />

			{editable && (
				<ButtonNew onClick={handleSubmit} className="w-full">
					{t("PREVIEW_SALARIES")}
				</ButtonNew>
			)}
		</div>
	);
};

export default SalaryBatchForm;
